/**
 * CallTrackingMetrics Field-Selection Allowlist (Story 22.3 AC #4 + AC #12;
 * Story 35.4 AC #3 — restructured from flat array → per-endpoint object).
 *
 * CTM's `/api/v1/accounts/{account_id}/calls.json` endpoint supports an
 * OPTIONAL `fields=...` query parameter for field selection. Per the
 * [official CTM Postman collection](https://postman.calltrackingmetrics.com/)
 * verified 2026-05-15 + 2026-05-21 and the [API Users Guide](https://github.com/calltracking/calltracking.github.io/blob/master/api_users_guide.md)
 * verified 2026-05-15 + 2026-05-21, the endpoint returns a default field
 * set plus optional opt-in fields when `?fields=` is provided.
 *
 * Conversation Intelligence fields (`transcription`, `transcription_text`,
 * `summary`, `agent_insights`, `spotted`, `likelihood`, plus Story 35.4's
 * `notes`, `call_path`, `first_touch`, `last_touch`, `last_location`,
 * `scorecards`) require paid CTM add-ons per the [CTM Conversation
 * Intelligence](https://www.calltrackingmetrics.com/features/conversation-intelligence/)
 * page verified 2026-05-15 + 2026-05-21. Requesting them without
 * entitlement returns either 422 (validation error — field-not-allowed-
 * for-this-subscription) or 200 with the fields populated as `null`
 * (per Round 2 Pre-mortem A).
 *
 * Story 35.4 extends the CI-gated set with 6 additional fields AND adds
 * 2 new entitlement-gate Sets:
 *  - `CTM_LEAD_REACTOR_GATED_FIELDS` (Story 35.4 — Batch 4)
 *  - `CTM_SCORECARD_GATED_FIELDS` (Story 35.4 — Batch 3 / Scorecards add-on)
 *
 * **Joining contract:** fields MUST be joined with `,` (comma) — NEVER
 * with `, ` (comma + space). CTM's URL parser treats the literal space
 * as part of the next field name and 422s on unrecognized field names.
 * The co-located test fuzzes against accidental space insertion.
 *
 * **NOTE: snake_case** — these are the wire-format field names CTM
 * expects on the URL. The transformer in `transformers.ts` rewrites them
 * to lowerCamelCase for the ProvenLeap record schema.
 *
 * **Story 35.4 restructure:** prior shape was a single flat readonly
 * array (used only by the `/calls.json` URL builder). Story 35.4 widens
 * the surface to 5 endpoint families — the new shape is a per-endpoint
 * object keyed by endpoint name (`calls | forms | sms | triggers |
 * integrations`). The existing Story 22.3 fields are preserved verbatim
 * under the `calls` key. Backwards compat: `CTM_FIELD_ALLOWLIST` (the
 * `calls` flat list) remains exported as a frozen alias for the existing
 * `/calls.json` URL builder and for the `metric-allowlist.test.ts`
 * build-time guard.
 */

/**
 * `/calls.json` field allowlist. Captures the core call attributes +
 * marketing attribution + customer geo + tracker + CI-gated fields +
 * Story 35.4 expansion fields (notes/call_path/first_touch/last_touch/
 * last_location/scorecards).
 *
 * Ordering matches the per-endpoint field table in
 * `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md`. Adding a
 * new field REQUIRES adding it to this allowlist + updating the
 * catalogue file's "Story Reference" column for traceability.
 */
const CTM_CALLS_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  // Identity / customer
  "id",
  "account_id",
  "sid",
  "caller_number",
  "caller_number_format",
  "name",
  "email",
  // Customer geo (PII-flagged at the sanitizer)
  "street",
  "city",
  "state",
  "postal_code",
  "country",
  "geo",
  // Call attributes
  "dial_status",
  "talk_time",
  "ring_time",
  "duration",
  "hold_time",
  "wait_time",
  "called_at",
  "unix_time",
  "day",
  "month",
  "hour",
  "is_new_caller",
  "redacted",
  "excluded",
  "likelihood",
  // Marketing attribution
  "source",
  "source_id",
  "source_tag",
  "tracking_number",
  "tracking_number_format",
  "tracking_number_bare",
  "tracking_label",
  "business_number",
  "business_label",
  "campaign",
  "medium",
  "keyword",
  "ad_group_id",
  "ad_match_type",
  "ad_content",
  "ad_network",
  "ad_format",
  "search",
  // UTM nested
  "paid",
  // Click IDs
  "ga",
  // Web visit context
  "webvisit",
  // Tag list + free-form notes
  "tag_list",
  "notes",
  // Conversation Intelligence — gated by `conversationIntelligenceEntitled`
  "transcription",
  "transcription_text",
  "summary",
  "agent_insights",
  "spotted",
  // Story 35.4 — Batch 3 CI-gated expansion fields
  "call_path",
  "first_touch",
  "last_touch",
  "last_location",
  // Scorecards add-on (Story 35.4 — gated by `scorecardsEnabled`)
  "scorecards",
  // Inline sale (opportunistic per AC #4 Endpoint Group D — present
  // when the agent has scored the call)
  "sale",
  // Story 35.4 — Batch 4 LeadReactor-gated expansion fields (present on
  // /calls.json when the call traversed a LeadReactor trigger). Gated
  // by `leadReactorsEnabled`.
  "reactor_flag",
  "reactor_trigger_name",
  "reactor_actions_count",
  "reactor_last_fired",
];

/**
 * `/forms.json` field allowlist. Story 35.4 — Batch 1 expansion adds
 * the FormReactor template + custom-field + duration + completion-state
 * + first/last-touch attribution fields. PII-bearing fields
 * (`form.custom[].value`) are redacted at the log boundary per AC #2.
 */
const CTM_FORMS_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  // Identity
  "id",
  "account_id",
  // Story 22.3 baseline form fields
  "created_at",
  "triggered_by",
  "email",
  "name",
  "phone",
  "landing_page_url",
  "referring_url",
  "tag_list",
  // Story 35.4 — Batch 1 expansion
  "custom",
  "form_id",
  "form_name",
  "duration",
  "completed",
  "first_touch",
  "last_touch",
];

/**
 * `/sms.json` field allowlist. Story 35.4 — Batch 2 expansion adds
 * granular per-message timestamps + thread_id + status + tag_list +
 * SMS-level attribution fields (source/medium/campaign).
 */
const CTM_SMS_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  // Identity
  "id",
  "account_id",
  // Story 22.3 baseline SMS fields
  "created_at",
  "direction",
  "caller_number",
  "tracking_number",
  "message",
  // Story 35.4 — Batch 2 expansion
  "sent_time",
  "received_time",
  "thread_id",
  "conversation_id",
  "status",
  "tag_list",
  "source",
  "medium",
  "campaign",
];

/**
 * `/triggers.json` field allowlist (Story 35.4 — Batch 4 NEW endpoint).
 * LeadReactor configuration records. Trigger names are user-defined
 * free-text (high CSV-injection risk; stripped via
 * `stripCsvInjectionPrefix` in the transformer).
 */
const CTM_TRIGGERS_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  "id",
  "name",
  "type",
  "status",
  "enabled",
  "conditions",
  "actions",
  "created_at",
  "updated_at",
  "last_fired_at",
];

/**
 * `/integrations.json` field allowlist (Story 35.4 — Batch 4 NEW
 * endpoint). 3rd-party partner connection records (Google Ads, HubSpot,
 * Salesforce, Zapier, etc.).
 */
const CTM_INTEGRATIONS_FIELD_ALLOWLIST: ReadonlyArray<string> = [
  "id",
  "name",
  "type",
  "status",
  "connected_at",
  "last_sync_at",
];

/**
 * Per-endpoint field allowlist (Story 35.4 AC #3 restructure). Each
 * value is the flat array of wire-format field names that endpoint
 * supports. Adding a new field REQUIRES adding it under the correct
 * endpoint key here.
 */
export const CTM_FIELD_ALLOWLIST_BY_ENDPOINT: {
  readonly calls: ReadonlyArray<string>;
  readonly forms: ReadonlyArray<string>;
  readonly sms: ReadonlyArray<string>;
  readonly triggers: ReadonlyArray<string>;
  readonly integrations: ReadonlyArray<string>;
} = {
  calls: CTM_CALLS_FIELD_ALLOWLIST,
  forms: CTM_FORMS_FIELD_ALLOWLIST,
  sms: CTM_SMS_FIELD_ALLOWLIST,
  triggers: CTM_TRIGGERS_FIELD_ALLOWLIST,
  integrations: CTM_INTEGRATIONS_FIELD_ALLOWLIST,
};

/**
 * Endpoint identifier for per-endpoint field-allowlist lookups.
 * Story 35.4 introduces 2 new endpoints; the union widens from 3 → 5.
 */
export type CtmFieldEndpoint = keyof typeof CTM_FIELD_ALLOWLIST_BY_ENDPOINT;

/**
 * Backwards-compat alias: the historical `CTM_FIELD_ALLOWLIST` flat
 * array exported by Story 22.3 was the `/calls.json` field list. The
 * `metric-allowlist.test.ts` build-time guard still validates against
 * this surface (the calls endpoint is where the wire fields with
 * `apiField:` mappings live). Story 35.4 preserves the alias verbatim.
 */
export const CTM_FIELD_ALLOWLIST: ReadonlyArray<string> = CTM_CALLS_FIELD_ALLOWLIST;

/**
 * The 12 Conversation Intelligence fields stripped from the `/calls.json`
 * field selection when `metadata.conversationIntelligenceEntitled
 * === false`. Story 35.4 widens the set from 6 → 12 by adding the
 * Batch 3 expansion fields (`notes`, `call_path`, `first_touch`,
 * `last_touch`, `last_location`) + the Scorecards probe field
 * (`scorecards` — also gated independently by `scorecardsEnabled`).
 *
 * Defined as a Set for O(1) `.has()` lookups in `computeCtmFields`.
 */
export const CTM_CI_GATED_FIELDS: ReadonlySet<string> = new Set<string>([
  // Story 22.3 baseline (6)
  "transcription",
  "transcription_text",
  "summary",
  "agent_insights",
  "spotted",
  "likelihood",
  // Story 35.4 — Batch 3 expansion (6)
  "notes",
  "call_path",
  "first_touch",
  "last_touch",
  "last_location",
  "scorecards",
]);

/**
 * The 4 LeadReactor-gated fields (Story 35.4 — Batch 4 NEW). Stripped
 * from the `/calls.json` field selection when `metadata.leadReactorsEnabled
 * === false`. These are flat fields on the `calls` record that flag
 * which LeadReactor trigger fired on which call (NOT the `/triggers.json`
 * endpoint records themselves — those are a separate gating axis at the
 * orchestration layer in `fetchMetrics`).
 */
export const CTM_LEAD_REACTOR_GATED_FIELDS: ReadonlySet<string> = new Set<string>([
  "reactor_flag",
  "reactor_trigger_name",
  "reactor_actions_count",
  "reactor_last_fired",
]);

/**
 * The 4 Scorecard-gated fields (Story 35.4 — Batch 3 add-on). Stripped
 * from the `/calls.json` field selection when `metadata.scorecardsEnabled
 * === false`. The umbrella `scorecards` field is ALSO in
 * `CTM_CI_GATED_FIELDS` so the stricter gate fires — either flag false
 * is sufficient to strip. The sub-field shapes
 * (`scorecard_id` / `scorecard_score` / `scorecard_passed` /
 * `scorecard_sections`) are documented here for completeness; CTM's
 * `/calls.json` returns them under the top-level `scorecards` array
 * shape per the Postman collection observed 2026-05-15.
 */
export const CTM_SCORECARD_GATED_FIELDS: ReadonlySet<string> = new Set<string>([
  "scorecard_id",
  "scorecard_score",
  "scorecard_passed",
  "scorecard_sections",
]);

/**
 * Compute the `fields=` URL-param value given the integration's
 * entitlement state. Returns a comma-joined string (NO spaces).
 *
 * **Story 35.4 signature extension:** the helper now takes the
 * `endpoint` (calls/forms/sms/triggers/integrations) as the first
 * parameter, plus 3 entitlement flags. The 3 flags map to the 3
 * entitlement gates Story 35.4 introduces:
 *  - `conversationIntelligenceEntitled` — strips CTM_CI_GATED_FIELDS (12 fields)
 *  - `leadReactorsEnabled` — strips CTM_LEAD_REACTOR_GATED_FIELDS (4 fields)
 *  - `scorecardsEnabled` — strips CTM_SCORECARD_GATED_FIELDS (4 fields)
 *
 * Each gate ONLY applies to the `calls` endpoint — `forms`, `sms`,
 * `triggers`, and `integrations` don't carry CI/LeadReactor/Scorecard
 * fields in the per-record shape (LeadReactor surfaces in `/triggers.json`
 * is gated at the orchestration layer instead of the field-selection
 * layer).
 *
 * Pure function — safe to call repeatedly; tests assert the exact join
 * shape via the co-located `fields.test.ts`.
 *
 * @param endpoint - Which CTM endpoint family the fields are for.
 * @param conversationIntelligenceEntitled - Read from
 *   `metadata.conversationIntelligenceEntitled` at the route boundary
 *   (set at connect time per Story 22.3 AC #2 probe + Story 35.4 cron
 *   re-probe).
 * @param leadReactorsEnabled - Story 35.4 AC #7 — read from
 *   `metadata.leadReactorsEnabled`. Optional, defaults to `false`
 *   (defensive: missing flag = treat as not entitled).
 * @param scorecardsEnabled - Story 35.4 AC #7 — read from
 *   `metadata.scorecardsEnabled`. Optional, defaults to `false`.
 * @returns Comma-joined field list with NO spaces.
 */
export function computeCtmFields(
  endpoint: CtmFieldEndpoint,
  conversationIntelligenceEntitled: boolean,
  leadReactorsEnabled = false,
  scorecardsEnabled = false,
): string {
  const baseAllowlist = CTM_FIELD_ALLOWLIST_BY_ENDPOINT[endpoint];
  // **Round 2 Edge Case Hunter HIGH fix:** apply CI gating to ALL
  // endpoints whose allowlist contains a CI-gated field. The Round 1
  // version returned the non-calls allowlist unfiltered, which on
  // /forms.json sends `first_touch,last_touch` (both CI-gated) and
  // either 422s the whole forms fetch OR exposes CI-gated data the
  // customer isn't paying for. LeadReactor + Scorecard gates are
  // narrower — those gated-field sets ONLY apply to the calls endpoint
  // by spec, so they remain conditional.
  const filtered = baseAllowlist.filter((field) => {
    // **Round 3 note on cross-endpoint CI gating:** the only overlap
    // between `CTM_CI_GATED_FIELDS` and non-`calls` endpoint allowlists
    // is `first_touch` / `last_touch` on `forms`. The catalogue at
    // `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md`
    // documents these as CI-gated only on the calls endpoint; on
    // `/forms.json` they MAY be plain UTM-attribution fields. We strip
    // them here defensively — the cost (forms attribution unavailable
    // to non-CI customers) is lower than the cost of a 422 breaking the
    // whole forms fetch. If future vendor docs confirm forms-side
    // first_touch/last_touch are unrestricted, lift the gate to a
    // per-endpoint CTM_CI_GATED_FIELDS_BY_ENDPOINT.calls set.
    if (!conversationIntelligenceEntitled && CTM_CI_GATED_FIELDS.has(field)) {
      return false;
    }
    if (endpoint !== "calls") return true;
    // calls-endpoint-specific gates below.
    if (!leadReactorsEnabled && CTM_LEAD_REACTOR_GATED_FIELDS.has(field)) {
      return false;
    }
    // Per AC #11 — the umbrella `scorecards` field AND the sub-field set
    // (CTM_SCORECARD_GATED_FIELDS) are stripped independently of CI when
    // scorecardsEnabled === false. The umbrella `scorecards` is also in
    // CTM_CI_GATED_FIELDS so the CI gate strips it too — but the
    // Scorecards gate is the stricter authority (always strips when
    // false, even when CI is entitled).
    if (!scorecardsEnabled && CTM_SCORECARD_GATED_FIELDS.has(field)) {
      return false;
    }
    if (!scorecardsEnabled && field === "scorecards") {
      return false;
    }
    return true;
  });
  return joinCtmFields(filtered);
}

/**
 * Join a field list using the strict `,` (no-space) separator that CTM's
 * URL parser requires. CRITICAL: `.join(",")` NOT `.join(", ")`. CTM's
 * parser treats a literal space as part of the next field name and 422s
 * on unrecognized field names. Extracted as a helper so the
 * `computeCtmFields` branches share the join contract.
 */
function joinCtmFields(fields: ReadonlyArray<string>): string {
  return fields.join(",");
}

/**
 * CTM API hard ceiling on `per_page` parameter (AC #4 + Round 5 Edge
 * Case 4). Exceeding triggers `HTTP 400` with the literal error
 * `"per_page limit is 150"`. DIFFERENT from CallRail 22.1's 250.
 *
 * Verified `_bmad/bmm/data/integration-metrics/calltrackingmetrics.md`
 * line 9 + observed against the CTM Postman collection 2026-05-15.
 */
export const CTM_PER_PAGE_MAX = 150;
